'use strict';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBackRounded,
  ArrowForwardRounded,
  LockRounded,
  LoginRounded,
  PersonRounded,
  PointOfSaleRounded,
  QrCodeScannerRounded,
  ReceiptLongRounded,
  SearchRounded,
  ShoppingCartCheckoutRounded,
  VerifiedUserRounded,
} from '@mui/icons-material';
import api from '../api/api';
import { clearAuthSession, commitAuthSession, getTenantId } from '../auth/authStorage';

const PDV_PERMISSION = 'pdv:terminal:ver';

function normalizePermission(value) {
  if (typeof value === 'string') return value.trim().toLowerCase();
  return String(value?.name || value?.permission || value?.code || value?.clave || '').trim().toLowerCase();
}

function tenantPermissions(tenant) {
  const source = Array.isArray(tenant?.permissions)
    ? tenant.permissions
    : Array.isArray(tenant?.permisos)
      ? tenant.permisos
      : [];
  return [...new Set(source.map(normalizePermission).filter(Boolean))];
}

function tenantHasPdvAccess(tenant) {
  return tenantPermissions(tenant).includes(PDV_PERMISSION);
}

const TURNSTILE_SCRIPT_ID = 'integranube-turnstile-script';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let turnstileScriptPromise = null;

function loadTurnstileScript() {
  if (typeof window === 'undefined') return Promise.reject(new Error('TURNSTILE_BROWSER_REQUIRED'));
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (turnstileScriptPromise) return turnstileScriptPromise;

  turnstileScriptPromise = new Promise((resolve, reject) => {
    let script = document.getElementById(TURNSTILE_SCRIPT_ID);
    const done = () => window.turnstile ? resolve(window.turnstile) : reject(new Error('TURNSTILE_API_NOT_AVAILABLE'));
    const fail = () => {
      turnstileScriptPromise = null;
      reject(new Error('TURNSTILE_SCRIPT_LOAD_FAILED'));
    };

    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    script.addEventListener('load', done, { once: true });
    script.addEventListener('error', fail, { once: true });
    window.setTimeout(() => { if (window.turnstile) done(); }, 0);
  });

  return turnstileScriptPromise;
}

const Turnstile = forwardRef(function Turnstile({ siteKey, onToken, onError }, ref) {
  const containerRef = useRef(null);
  const widgetRef = useRef(null);
  const callbacksRef = useRef({ onToken, onError });

  useEffect(() => { callbacksRef.current = { onToken, onError }; }, [onToken, onError]);

  useImperativeHandle(ref, () => ({
    reset() {
      try {
        if (widgetRef.current != null && window.turnstile) window.turnstile.reset(widgetRef.current);
      } catch (_) {}
    },
  }), []);

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const turnstile = await loadTurnstileScript();
        if (cancelled || !containerRef.current) return;
        widgetRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action: 'login',
          theme: 'light',
          size: 'normal',
          callback: (value) => callbacksRef.current.onToken?.(String(value || '').trim()),
          'expired-callback': () => callbacksRef.current.onToken?.(''),
          'timeout-callback': () => callbacksRef.current.onToken?.(''),
          'error-callback': (code) => {
            callbacksRef.current.onToken?.('');
            callbacksRef.current.onError?.(String(code || 'unknown'));
          },
        });
      } catch (error) {
        if (!cancelled) callbacksRef.current.onError?.(error?.message || 'TURNSTILE_SCRIPT_LOAD_FAILED');
      }
    })();

    return () => {
      cancelled = true;
      try {
        if (widgetRef.current != null && window.turnstile) window.turnstile.remove(widgetRef.current);
      } catch (_) {}
      widgetRef.current = null;
    };
  }, [siteKey]);

  return <Box ref={containerRef} sx={{ minHeight: 65 }} />;
});

function normalizeLoginIdentifier(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  const rutCompact = upper.replace(/\./g, '').replace(/-/g, '').replace(/\s+/g, '').replace(/[^0-9K]/g, '');

  if (!raw.includes('@') && /^[0-9]{7,8}[0-9K]$/.test(rutCompact)) {
    const body = rutCompact.slice(0, -1).replace(/^0+/, '') || '0';
    return `${body}-${rutCompact.slice(-1)}`;
  }

  return raw.toLowerCase();
}

function tenantName(tenant) {
  return tenant?.name || tenant?.nombre || tenant?.nombre_fantasia || tenant?.razon_social || tenant?.tenant_name || tenant?.empresa || 'Empresa';
}

function tenantInitials(tenant) {
  const words = String(tenantName(tenant)).trim().split(/\s+/).filter(Boolean);
  return `${words[0]?.[0] || 'I'}${words[1]?.[0] || words[0]?.[1] || 'N'}`.toUpperCase();
}

export default function Login({ onLogin }) {
  const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
  const [step, setStep] = useState('login');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [identifier, setIdentifier] = useState(() => localStorage.getItem('rememberedLogin') || '');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(() => Boolean(localStorage.getItem('rememberedLogin')));
  const [token, setToken] = useState('');
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const turnstileRef = useRef(null);

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenants
      .filter((tenant) => getTenantId(tenant))
      .filter(tenantHasPdvAccess)
      .filter((tenant) => !q || JSON.stringify(tenant).toLowerCase().includes(q));
  }, [tenants, search]);

  const resetTurnstile = () => {
    setTurnstileToken('');
    try { turnstileRef.current?.reset?.(); } catch (_) {}
  };

  const submit = async (event) => {
    event.preventDefault();
    const loginId = normalizeLoginIdentifier(identifier);

    if (!loginId || !password) {
      setError('Ingrese correo o RUT y contraseña.');
      return;
    }

    if (siteKey && !turnstileToken) {
      setError('Complete la verificación de seguridad.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/auth/login', {
        email: loginId,
        identifier: loginId,
        login: loginId,
        password,
        turnstileToken: turnstileToken || undefined,
      });

      if (!data?.token) throw new Error('TOKEN_MISSING');

      const me = await api.get('/auth/me', {
        headers: { Authorization: `Bearer ${data.token}` },
        skipTenant: true,
        timeout: 6000,
      });

      const allAllowedTenants = Array.isArray(me?.data?.allowedTenants)
        ? me.data.allowedTenants
        : Array.isArray(me?.data?.allowed_tenants)
          ? me.data.allowed_tenants
          : [];

      // En el PDV solo mostramos empresas cuyo perfil tenga acceso explícito
      // al nuevo terminal. `pos:terminal:ver` pertenece al POS Retail anterior.
      const allowedTenants = allAllowedTenants.filter(tenantHasPdvAccess);
      const meUser = me?.data?.user || null;

      if (remember) localStorage.setItem('rememberedLogin', loginId);
      else localStorage.removeItem('rememberedLogin');

      if (!allowedTenants.length) {
        setError(`El usuario no tiene empresas habilitadas con el permiso ${PDV_PERMISSION}.`);
        resetTurnstile();
        return;
      }

      setToken(data.token);
      setUser(meUser);
      setTenants(allowedTenants);

      if (allowedTenants.length === 1) {
        const selectedTenant = {
          ...allowedTenants[0],
          permissions: tenantPermissions(allowedTenants[0]),
        };
        commitAuthSession({ token: data.token, user: meUser, tenant: selectedTenant });
        await onLogin?.({ token: data.token, user: meUser, tenant: selectedTenant });
        return;
      }

      setStep('tenant');
    } catch (err) {
      clearAuthSession();
      resetTurnstile();
      setError(err?.response?.data?.error || err?.response?.data?.message || 'Credenciales incorrectas o error de servidor.');
    } finally {
      setLoading(false);
    }
  };

  const chooseTenant = async (tenant) => {
    setLoading(true);
    setError('');
    try {
      if (!tenantHasPdvAccess(tenant)) {
        throw new Error('PDV_PERMISSION_DENIED');
      }

      const selectedTenant = {
        ...(tenant || {}),
        permissions: tenantPermissions(tenant),
      };

      commitAuthSession({ token, user, tenant: selectedTenant });
      await onLogin?.({ token, user, tenant: selectedTenant });
    } catch {
      clearAuthSession();
      setError('No fue posible seleccionar la empresa.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    clearAuthSession();
    setStep('login');
    setToken('');
    setUser(null);
    setTenants([]);
    setPassword('');
    setTurnstileToken('');
    setError('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        p: { xs: 1.5, sm: 2.5 },
        position: 'relative',
        overflow: 'hidden',
        bgcolor: '#f5f9ff',
        background: 'linear-gradient(145deg, #f8fbff 0%, #edf6ff 52%, #f7fbff 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 520,
          height: 520,
          borderRadius: '50%',
          top: -250,
          left: -170,
          background: 'radial-gradient(circle, rgba(0,181,255,.16) 0%, rgba(0,181,255,0) 70%)',
          pointerEvents: 'none',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          width: 620,
          height: 620,
          borderRadius: '50%',
          right: -280,
          bottom: -340,
          background: 'radial-gradient(circle, rgba(20,93,255,.13) 0%, rgba(20,93,255,0) 72%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: { xs: 3, sm: 4.5 },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '440px minmax(0, 1fr)' },
            border: '1px solid #dce8f5',
            boxShadow: '0 28px 90px rgba(28, 72, 118, .16)',
            bgcolor: '#fff',
          }}
        >
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              p: 4,
              minHeight: 650,
              flexDirection: 'column',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden',
              borderRight: '1px solid #dce8f5',
              background: 'linear-gradient(160deg, #fafdff 0%, #eaf6ff 58%, #e2f2ff 100%)',
              '&::after': {
                content: '""',
                position: 'absolute',
                width: 300,
                height: 300,
                borderRadius: '50%',
                right: -120,
                bottom: -130,
                bgcolor: 'rgba(0, 154, 255, .08)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  bgcolor: '#fff',
                  border: '1px solid #e1edf8',
                  borderRadius: 4,
                  p: 1.5,
                  boxShadow: '0 14px 40px rgba(35, 91, 145, .10)',
                }}
              >
                <Box
                  component="img"
                  src="/logo-pdv.png"
                  alt="IntegraNube PDV"
                  sx={{
                    display: 'block',
                    width: '100%',
                    height: 'auto',
                    maxHeight: 285,
                    objectFit: 'contain',
                  }}
                />
              </Box>

              <Typography variant="h5" fontWeight={950} sx={{ mt: 3.2, color: '#0b2d59' }}>
                Punto de Venta rápido y simple
              </Typography>
              <Typography sx={{ mt: 0.8, color: '#5d7188', lineHeight: 1.55 }}>
                Diseñado para operar con teclado, lector de códigos de barra y flujo de cobro rápido.
              </Typography>

              <Box
                sx={{
                  mt: 3,
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 1.15,
                }}
              >
                {[
                  { icon: <QrCodeScannerRounded />, label: 'Escáner rápido' },
                  { icon: <ShoppingCartCheckoutRounded />, label: 'Venta simple' },
                  { icon: <PointOfSaleRounded />, label: 'Caja integrada' },
                  { icon: <ReceiptLongRounded />, label: 'Boleta y factura' },
                ].map((item) => (
                  <Stack
                    key={item.label}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{
                      minHeight: 54,
                      px: 1.25,
                      py: 0.9,
                      borderRadius: 2.5,
                      bgcolor: 'rgba(255,255,255,.78)',
                      border: '1px solid rgba(159, 196, 231, .55)',
                      color: '#173c68',
                    }}
                  >
                    <Box sx={{ color: '#0878f9', display: 'grid', placeItems: 'center' }}>{item.icon}</Box>
                    <Typography variant="body2" fontWeight={850}>{item.label}</Typography>
                  </Stack>
                ))}
              </Box>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ position: 'relative', zIndex: 1, color: '#6a7f95' }}>
              <VerifiedUserRounded sx={{ fontSize: 18, color: '#0f8be8' }} />
              <Typography variant="caption" fontWeight={700}>Acceso seguro conectado a IntegraNube</Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 2.5, sm: 4.5, md: 5.5 },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minWidth: 0,
              minHeight: { xs: '100vh', sm: 610, md: 650 },
            }}
          >
            <Box
              component="img"
              src="/logo-pdv.png"
              alt="IntegraNube PDV"
              sx={{
                display: { xs: 'block', md: 'none' },
                width: '100%',
                maxWidth: 300,
                maxHeight: 190,
                objectFit: 'contain',
                mx: 'auto',
                mb: 2.5,
              }}
            />

            {step === 'login' ? (
              <Box component="form" onSubmit={submit} sx={{ width: '100%', maxWidth: 520, mx: 'auto' }}>
                <Stack spacing={2.15}>
                  <Box sx={{ mb: 0.4 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mb: 0.65 }}>
                      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: '#08b8df' }} />
                      <Typography variant="overline" color="primary" fontWeight={950} sx={{ letterSpacing: 1.2 }}>
                        IntegraNube PDV
                      </Typography>
                    </Stack>
                    <Typography variant="h4" fontWeight={950} sx={{ color: '#102d52', letterSpacing: '-.5px' }}>
                      Acceso al Punto de Venta
                    </Typography>
                    <Typography color="text.secondary" sx={{ mt: 0.7 }}>
                      Ingrese con las mismas credenciales utilizadas en IntegraNube.
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    label="Correo o RUT"
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    autoComplete="username"
                    autoFocus
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><PersonRounded color="primary" /></InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 56,
                        borderRadius: 2.5,
                        bgcolor: '#fbfdff',
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Contraseña"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><LockRounded color="primary" /></InputAdornment>,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: 56,
                        borderRadius: 2.5,
                        bgcolor: '#fbfdff',
                      },
                    }}
                  />

                  {siteKey ? (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        px: 1,
                        py: 0.7,
                        border: '1px solid #e1e9f2',
                        bgcolor: '#fbfdff',
                        borderRadius: 2.5,
                      }}
                    >
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={siteKey}
                        onToken={setTurnstileToken}
                        onError={() => setError('No se pudo completar la verificación de seguridad. Intente nuevamente.')}
                      />
                    </Box>
                  ) : (
                    <Alert severity="warning" sx={{ borderRadius: 2.5 }}>
                      Turnstile no está configurado en este entorno.
                    </Alert>
                  )}

                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <FormControlLabel
                      control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} />}
                      label={<Typography variant="body2" fontWeight={700}>Recordar usuario</Typography>}
                    />
                    <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: '#6c8096' }}>
                      <VerifiedUserRounded sx={{ fontSize: 17, color: '#00aa86' }} />
                      <Typography variant="caption" fontWeight={700}>Conexión segura</Typography>
                    </Stack>
                  </Stack>

                  {error && <Alert severity="error" sx={{ borderRadius: 2.5 }}>{error}</Alert>}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading || (siteKey && !turnstileToken)}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <LoginRounded />}
                    sx={{
                      py: 1.35,
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontSize: 16,
                      fontWeight: 950,
                      boxShadow: '0 12px 28px rgba(17, 111, 238, .22)',
                    }}
                  >
                    {loading ? 'Validando…' : 'Ingresar al PDV'}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Stack spacing={2.2} sx={{ width: '100%', maxWidth: 560, mx: 'auto' }}>
                <Box>
                  <Typography variant="overline" color="primary" fontWeight={950} sx={{ letterSpacing: 1.1 }}>
                    Selección de empresa
                  </Typography>
                  <Typography variant="h4" fontWeight={950} sx={{ color: '#102d52', letterSpacing: '-.5px' }}>
                    ¿Dónde se abrirá el PDV?
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.7 }}>
                    {user?.name || user?.nombre || user?.email || 'Usuario conectado'}
                  </Typography>
                </Box>

                {tenants.length > 4 && (
                  <TextField
                    size="small"
                    placeholder="Buscar empresa…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><SearchRounded /></InputAdornment>,
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.3, bgcolor: '#fbfdff' } }}
                  />
                )}

                {error && <Alert severity="error" sx={{ borderRadius: 2.5 }}>{error}</Alert>}

                <Stack spacing={1.15} sx={{ maxHeight: 390, overflowY: 'auto', pr: 0.5 }}>
                  {options.map((tenant) => (
                    <Card
                      key={getTenantId(tenant)}
                      variant="outlined"
                      sx={{
                        borderRadius: 2.7,
                        borderColor: '#dce7f2',
                        boxShadow: 'none',
                        transition: 'transform .15s ease, border-color .15s ease, box-shadow .15s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          borderColor: '#87baf4',
                          boxShadow: '0 8px 24px rgba(33, 91, 148, .08)',
                        },
                      }}
                    >
                      <CardActionArea onClick={() => !loading && chooseTenant(tenant)}>
                        <CardContent sx={{ py: 1.45 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                width: 44,
                                height: 44,
                                bgcolor: '#e5f3ff',
                                color: '#116fdc',
                                fontWeight: 950,
                                border: '1px solid #c9e3fb',
                              }}
                            >
                              {tenantInitials(tenant)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography fontWeight={900} noWrap>{tenantName(tenant)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {tenant?.rut || tenant?.rut_empresa || getTenantId(tenant)}
                              </Typography>
                            </Box>
                            {loading ? <CircularProgress size={20} /> : <ArrowForwardRounded color="primary" />}
                          </Stack>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  ))}
                </Stack>

                <Button
                  variant="outlined"
                  startIcon={<ArrowBackRounded />}
                  onClick={goBack}
                  disabled={loading}
                  sx={{ alignSelf: 'flex-start', borderRadius: 2.3, textTransform: 'none', fontWeight: 850 }}
                >
                  Cambiar cuenta
                </Button>
              </Stack>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
